import { createReducer, on } from '@ngrx/store';

import { goToProjects, setProject } from '../../core/store';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import {
	activateExpositions,
	autoMappingSuccess,
	clearExpositionsData,
	createExposition,
	createExpositionFail,
	createExpositionSuccess,
	deactivateExpositions,
	deleteExpositionAccess,
	deleteExpositionAccessSuccess,
	deleteExpositionFromView,
	deleteExpositions,
	expositionActionsCompleted,
	fetchAvailableExpositionsListSuccess,
	fetchColumnConfigListSuccess,
	fetchDataBlockColumnsSuccess,
	fetchExpositionAccessSuccess,
	fetchExpositionColumnsSuccess,
	fetchExpositionDataBlockListSuccess,
	fetchExpositionEndpointMetadataSuccess,
	fetchExpositionHistorySuccess,
	fetchExpositionMetadataSuccess,
	fetchExpositionOnHistoryNavigationSuccess,
	fetchExpositionSuccess,
	fetchExpositionViewOnEndpointDetailsTrigger,
	fetchExpositionViewOnEndpointDetailsTriggerSuccess,
	fetchJobErrorMessageFail,
	fetchJobErrorMessageSuccess,
	getColumnConfigList,
	getDataBlocksColumns,
	getJobErrorMessage,
	initExpositionsList,
	publicationActionSuccess,
	publishExpositions,
	refreshAfterErrorSuccess,
	refreshDatablocksSuccess,
	refreshExpositionAfterWS,
	selectExposition,
	updateExpositionParams,
	updateExpositionsData,
	updateExpositionWithoutJobTracking,
	updateExpositionRunningStatus,
} from './actions';
import { ExpositionOriginalSliceState } from './expositions.state';

export const expositionOriginalReducer =
	createReducer<ExpositionOriginalSliceState>(
		new ExpositionOriginalSliceState(),
		on(goToProjects, setProject, (state) => state.clear()),
		on(selectExposition, (state) =>
			state.setInViewExposition(ExpositionEntity.build())
		),
		on(initExpositionsList, (state) => state.toggleLoading(true)),
		on(
			deleteExpositions,
			deleteExpositionFromView,
			deleteExpositionAccess,
			publishExpositions,
			updateExpositionsData,
			clearExpositionsData,
			activateExpositions,
			deactivateExpositions,
			updateExpositionParams,
			updateExpositionWithoutJobTracking,
			(state) => state.setActionInProgress(true)
		),
		on(expositionActionsCompleted, (state) => state.setActionInProgress(false)),
		on(fetchAvailableExpositionsListSuccess, (state, action) =>
			state
				.toggleLoading(false)
				.setExpositionList(action.list)
				.setCanCreateExposition(action.canCreate)
		),
		on(fetchExpositionDataBlockListSuccess, (state, action) =>
			state
				.setDatablockList(action.datablocks)
				.setExpositionList(action.availableExpositions)
		),
		on(getDataBlocksColumns, (state) =>
			state.toggleLoadingDatablockColumns(true)
		),
		on(fetchDataBlockColumnsSuccess, (state, action) =>
			state
				.toggleLoadingDatablockColumns(false)
				.updateDatablockListWithColumns(action.datablockId, action.columns)
		),
		on(getColumnConfigList, (state) => state.toggleLoading(true)),
		on(fetchColumnConfigListSuccess, (state, action) =>
			state.setColumnConfigList(action.list).toggleLoading(false)
		),
		on(createExposition, (state) => state.toggleIsSavingExposition(true)),
		on(createExpositionSuccess, (state) => state.resetStepper()),
		on(createExpositionFail, (state) => state.toggleIsSavingExposition(false)),
		on(fetchExpositionSuccess, (state, action) =>
			state
				.setInViewExposition(action.exposition)
				.setExpositionList(action.availableExpositions)
				.setExpositionViewPermissions(action.permissions)
		),
		on(fetchExpositionColumnsSuccess, (state, action) =>
			state
				.setInViewExposition(state.inViewExposition.setColumns(action.columns))
				.setExpositionViewPermissions(action.permissions)
		),
		on(fetchExpositionMetadataSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setMetadata(action.metadata)
			)
		),
		on(fetchExpositionEndpointMetadataSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setEndpointMetadata(action.metadata)
			)
		),
		on(fetchExpositionAccessSuccess, (state, action) => {
			const { exposition } = action;
			const updated = exposition.setAccessPoints(
				exposition.accessPoints.setCurrentConfig(action.access)
			);
			return state
				.setInViewExposition(updated)
				.setExpositionViewPermissions(action.permissions);
		}),
		on(deleteExpositionAccessSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setAccessPoints(
					state.inViewExposition.accessPoints.deleteAccess(action.accessId)
				)
			)
		),
		on(updateExpositionRunningStatus, (state, action) =>
			state.setExpositionList(
				state.expositions.setApiStatusRunningOnItem(action.expositionId, true)
			)
		),
		on(publishExpositions, (state, action) =>
			action.expositionIds.reduce(
				(acc, expositionId) =>
					acc.setExpositionList(
						acc.expositions.setActionInProgressOnItem(expositionId, true)
					),
				state
			)
		),
		on(updateExpositionParams, (state) =>
			state.setInViewExposition(
				state.inViewExposition.setPublishedApi(
					state.inViewExposition.published.addInProgressRowToUpdates()
				)
			)
		),
		on(updateExpositionsData, (state, action) =>
			action.expositionIds
				.reduce(
					(acc, expositionId) =>
						acc.setExpositionList(
							acc.expositions.setActionInProgressOnItem(expositionId, true)
						),
					state
				)
				.setInViewExposition(
					state.inViewExposition.setPublishedApi(
						state.inViewExposition.published.addInProgressRowToUpdates()
					)
				)
		),
		on(refreshDatablocksSuccess, (state, action) =>
			state.setDatablockList(action.datablocks)
		),
		on(publicationActionSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setPublishedApi(
					state.inViewExposition.published.updateStatus(action.status)
				)
			)
		),
		on(refreshExpositionAfterWS, (state, action) =>
			state.setExpositionList(
				state.expositions.resetRunningActionOn(action.expositionId)
			)
		),
		on(autoMappingSuccess, (state, action) =>
			state.setUnmappedConsumers(action.consumersToMap)
		),
		on(fetchExpositionHistorySuccess, (state, action) =>
			state.setHistory(action.history)
		),
		on(
			fetchExpositionOnHistoryNavigationSuccess,
			refreshAfterErrorSuccess,
			(state, action) => state.setInViewExposition(action.exposition)
		),
		on(getJobErrorMessage, (state) => state.setIsLoadingErrorLogs(true)),
		on(fetchJobErrorMessageFail, (state) => state.setIsLoadingErrorLogs(false)),
		on(fetchJobErrorMessageSuccess, (state, action) =>
			state
				.setHistory(
					state.history.updateJobLog(action.id.toString(), action.message)
				)
				.setIsLoadingErrorLogs(false)
		),
		on(fetchExpositionViewOnEndpointDetailsTriggerSuccess, (state, action) =>
			state
				.setInViewExposition(action.exposition)
				.toggleLoadingMetadataInfo(false)
		),
		on(fetchExpositionViewOnEndpointDetailsTrigger, (state) =>
			state.toggleLoadingMetadataInfo(true)
		)
	);
